import {
  Button,
  Tooltip,
  useTheme,
} from '@prio365/prio365-react-library';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Flex from '../../../../../../components/Flex';
import UserAvatar from '../../../../../../components/UserAvatar';
import { PrioShareDto, PrioShareRecipientDto } from '../../../../../../models/PrioShare';
import { PrioTheme } from '../../../../../../theme/types';
import { makePrioStyles } from '../../../../../../theme/utils';
import PrioShareCollapsibleSection from '../PrioShareCollapsibleSection';
import PrioShareSelectableFileList from '../PrioShareSelectableFileList';
import PrioShareRecipientDrawer from '../PrioShareRecipientDrawer';
import { formatDate } from '../../../../../../util/helpers';

const useStyles = makePrioStyles((theme: PrioTheme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: '100%',
    gap: theme.spacing.regular,
    padding: `${theme.spacing.small}px 0`,
    overflow: 'auto',
  },
  messageText: {
    fontSize: theme.font.fontSize.small,
    color: theme.colors.application.typography.muted,
  },
  label: {
    fontSize: theme.font.fontSize.small,
    color: theme.colors.application.typography.default,
  },
  settingsValue: {
    color: theme.colors.application.typography.default,
    fontSize: theme.font.fontSize.small,
  },
}));

interface PrioShareDetailViewProps {
  item: PrioShareDto;
  projectExtensionId: string;
  onBack: () => void;
  onDeactivate?: (item: PrioShareDto) => void;
  onRecreate?: (item: PrioShareDto) => void;
  isRecreating?: boolean;
}

const emailToDisplayName = (email: string): string => {
  const local = email.split('@')[0] ?? '';
  if (!local) return email;
  const parts = local.split(/[._-]/);
  return parts.length >= 2 ? `${parts[0]} ${parts[1]}` : local;
};

const PrioShareDetailView: React.FC<PrioShareDetailViewProps> = ({
  item,
  projectExtensionId,
  onBack,
  onDeactivate,
  onRecreate,
  isRecreating,
}) => {
  //#region ------------------------------ Defaults
  const classes = useStyles();
  const theme = useTheme<PrioTheme>();
  const { t } = useTranslation();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const [selectedRecipient, setSelectedRecipient] = useState<PrioShareRecipientDto | null>(null);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  //#endregion

  //#region ------------------------------ Effects
  //#endregion

  return (
    <div className={classes.root}>
      <Flex.Column childrenGap={theme.spacing.regular}>
        {/* Message text */}
        {item.messageText && (
          <p className={classes.messageText}>{item.messageText}</p>
        )}

        {/* Dateien section */}
        <PrioShareCollapsibleSection
          title={t('common:widgetArea.prioShareWidget.createForm.filesLabel')}
          count={(item.files ?? []).length}
        >
          <PrioShareSelectableFileList
            files={item.files ?? []}
            projectExtensionId={projectExtensionId}
            prioShareId={item.prioShareId}
          />
        </PrioShareCollapsibleSection>

        {/* Empfänger section */}
        <PrioShareCollapsibleSection
          title={t(
            'common:widgetArea.prioShareWidget.createForm.recipientsLabel'
          )}
          count={(item.recipients ?? []).length}
        >
          <Flex.Row flexWrap="wrap">
            {(item.recipients ?? []).map((recipient, index) => (
              <Tooltip
                key={recipient.prioShareRecipientId}
                overlay={`${recipient.email} — ${t('common:widgetArea.prioShareWidget.detailView.clickForDetails')}`}
                placement="top"
              >
                <div
                  role="button"
                  tabIndex={0}
                  style={{
                    marginLeft: index > 0 ? -theme.spacing.small : 0,
                    position: 'relative',
                    zIndex: (item.recipients ?? []).length - index,
                    cursor: 'pointer',
                  }}
                  onClick={() => setSelectedRecipient(recipient)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelectedRecipient(recipient);
                    }
                  }}
                >
                  <UserAvatar
                    size="medium"
                    displayName={emailToDisplayName(recipient.email)}
                    backgroundColor={theme.colors.base.blue.default}
                    showGreenCheckMark={recipient.downloads.some(
                      (d) => d.downloadSuccessful
                    )}
                  />
                </div>
              </Tooltip>
            ))}
          </Flex.Row>
        </PrioShareCollapsibleSection>

        {/* Einstellungen section */}
        <PrioShareCollapsibleSection
          title={t(
            'common:widgetArea.prioShareWidget.createForm.settingsLabel'
          )}
        >
          <Flex.Column childrenGap={12}>
            {/* E-Mail */}
            <Flex.Row alignItems="center" justifyContent="space-between">
              <span className={classes.label}>
                {t(
                  'common:widgetArea.prioShareWidget.createForm.notificationEmailsLabel'
                )}
              </span>
              <span className={classes.settingsValue}>
                {item.notificationEmails?.join(', ') ?? '—'}
              </span>
            </Flex.Row>

            {/* Enddatum */}
            <Flex.Row alignItems="center" justifyContent="space-between">
              <span className={classes.label}>
                {t(
                  'common:widgetArea.prioShareWidget.createForm.expirationDateLabel'
                )}
              </span>
              <span className={classes.settingsValue}>
                {item.expirationDate ? formatDate(item.expirationDate) : '—'}
              </span>
            </Flex.Row>

            {/* Erstellt am */}
            <Flex.Row alignItems="center" justifyContent="space-between">
              <span className={classes.label}>
                {t(
                  'common:widgetArea.prioShareWidget.detailView.creationDateLabel'
                )}
              </span>
              <span className={classes.settingsValue}>
                {item.creationDate ? formatDate(item.creationDate) : '—'}
              </span>
            </Flex.Row>
          </Flex.Column>
        </PrioShareCollapsibleSection>
      </Flex.Column>
      <Flex.Row justifyContent="space-between">
        <Button type="default" onClick={onBack}>
          {t('common:actions.cancel')}
        </Button>
        {item.state === 'active' && onDeactivate && (
          <Button type="danger" onClick={() => onDeactivate(item)}>
            {t('common:widgetArea.prioShareWidget.deactivateConfirmOk')}
          </Button>
        )}
        {(item.state === 'expired' || item.state === 'inactive') &&
          onRecreate && (
            <Button type="primary" onClick={() => onRecreate(item)} loading={isRecreating} disabled={isRecreating}>
              {t('common:widgetArea.prioShareWidget.recreate')}
            </Button>
          )}
      </Flex.Row>
      <PrioShareRecipientDrawer
        recipient={selectedRecipient}
        visible={selectedRecipient !== null}
        onClose={() => setSelectedRecipient(null)}
      />
    </div>
  );
};

export default PrioShareDetailView;
